import { a as __rest } from '../../_tslib.esm.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef, useId, useRef, useState, useCallback } from 'react';
import * as RadixPopover from '@radix-ui/react-popover';
import popoverStyles from './Popover.styles.esm.js';
import { cn } from '../../utils/cn.esm.js';

const FOCUSABLE_ELEMENTS_SELECTOR = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
const Popover = forwardRef((_a, ref) => {
  var _b;
  var {
      children,
      trigger,
      className,
      inverse = false,
      isOpen,
      onOpenAutoFocus,
      onOpenChange,
      hasAsChild = true
    } = _a,
    props = __rest(_a, ["children", "trigger", "className", "inverse", "isOpen", "onOpenAutoFocus", "onOpenChange", "hasAsChild"]);
  const generatedId = useId();
  const contentId = `popover-content-${generatedId}`;
  const classes = popoverStyles({
    inverse,
    class: className
  });
  const contentRef = useRef(null);
  const [localOpen, setLocalOpen] = useState(false);
  const handleOpenAutoFocus = useCallback(event => {
    var _a, _b;
    event.preventDefault();
    const focusable = (_a = contentRef.current) === null || _a === void 0 ? void 0 : _a.querySelector(FOCUSABLE_ELEMENTS_SELECTOR);
    if (focusable) {
      focusable.focus();
    } else {
      (_b = contentRef.current) === null || _b === void 0 ? void 0 : _b.focus();
    }
  }, []);
  const handleKeyDownTrigger = useCallback(e => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      if (isOpen === undefined) {
        setLocalOpen(prev => !prev);
      } else {
        onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(!isOpen);
      }
    }
  }, [isOpen, onOpenChange]);
  const getFocusableElementsInContent = useCallback(() => {
    var _a;
    return (_a = contentRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll(FOCUSABLE_ELEMENTS_SELECTOR);
  }, []);
  const getExternalFocusableElements = useCallback(() => {
    const allElements = document.querySelectorAll(FOCUSABLE_ELEMENTS_SELECTOR);
    return Array.from(allElements).filter(element => {
      var _a;
      return !((_a = contentRef.current) === null || _a === void 0 ? void 0 : _a.contains(element));
    });
  }, []);
  const getTriggerElement = useCallback(() => {
    return document.querySelector(`[aria-controls="${contentId}"]`);
  }, [contentId]);
  const handleForwardTab = useCallback((e, lastElement) => {
    if (e.target !== lastElement) return false;
    e.preventDefault();
    const externalElements = getExternalFocusableElements();
    const triggerElement = getTriggerElement();
    if (!triggerElement) return true;
    const triggerIndex = externalElements.indexOf(triggerElement);
    const nextElement = externalElements[triggerIndex + 1];
    // Focus next element after trigger, or fallback to first element
    const elementToFocus = nextElement || externalElements[0];
    elementToFocus === null || elementToFocus === void 0 ? void 0 : elementToFocus.focus();
    return true;
  }, [getExternalFocusableElements, getTriggerElement]);
  const handleBackwardTab = useCallback((e, firstElement) => {
    if (e.target !== firstElement) return false;
    e.preventDefault();
    const triggerElement = getTriggerElement();
    if (triggerElement) {
      triggerElement.focus();
    }
    return true;
  }, [getTriggerElement]);
  const handleKeyDownContent = useCallback(e => {
    if (e.key !== "Tab") return;
    const focusableElements = getFocusableElementsInContent();
    if (!focusableElements || focusableElements.length === 0) return;
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    if (e.shiftKey) {
      handleBackwardTab(e, firstElement);
    } else {
      handleForwardTab(e, lastElement);
    }
  }, [getFocusableElementsInContent, handleBackwardTab, handleForwardTab]);
  const handleOpenChange = open => {
    // Always call when changed
    onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(open);
    if (isOpen === undefined) {
      setLocalOpen(open);
    }
  };
  const handleFocusOutside = event => {
    event.preventDefault();
  };
  const actualOpen = isOpen !== undefined ? isOpen : localOpen;
  return jsxs(RadixPopover.Root, {
    open: actualOpen,
    onOpenChange: handleOpenChange,
    children: [jsx(RadixPopover.Trigger, {
      style: {
        width: ((_b = props === null || props === void 0 ? void 0 : props.style) === null || _b === void 0 ? void 0 : _b.width) || undefined,
        height: "fix-content"
      },
      asChild: hasAsChild,
      "aria-haspopup": "dialog",
      "aria-expanded": actualOpen,
      "aria-controls": isOpen || localOpen ? contentId : undefined,
      onKeyDown: handleKeyDownTrigger,
      children: trigger
    }), jsx(RadixPopover.Content, Object.assign({
      id: contentId,
      ref: contentRef,
      className: cn(classes.content(), className),
      sideOffset: hasAsChild ? 8 : -8,
      onOpenAutoFocus: onOpenAutoFocus || handleOpenAutoFocus,
      onKeyDown: handleKeyDownContent,
      onFocusOutside: handleFocusOutside,
      role: "dialog"
    }, props, {
      children: children
    }))]
  });
});
Popover.displayName = "Popover";

export { Popover };
